// ─── src/FnfSurveyView.tsx ───────────────────────────────────────────────────
//
// FNF "Entrepreneurship in Syria 2026" survey view. Stats cards + tallies
// for the highest-signal columns, a searchable/filterable table, a detail
// modal that dumps every field, and CSV export.

import { useMemo, useState } from "react";
import {
  Download, Search, X,
  ClipboardList, MapPin, TrendingUp, Users,
} from "lucide-react";
import {
  useDeleteFnfSurveyResponse,
  useFnfSurveyResponses,
  useFnfSurveyStats,
} from "./hooks/useFnfSurvey";
import { Pagination } from "./components/dashboard/Pagination";
import { FnfSheetTable } from "./components/dashboard/FnfSheetTable";
import { StatCard } from "./components/ui/StatCard";
import { TableSkeleton } from "./components/ui/Skeleton";
import type { FnfSurveyResponse } from "./api/fnfSurvey";

const LIMIT = 20;

// ── CSV export (flattens every column of every row) ─────────────────────────
function exportCsv(rows: FnfSurveyResponse[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const arr = (v: string[] | undefined) => (v ?? []).join(" | ");
  const obj = (v: Record<string, string> | undefined) =>
    Object.entries(v ?? {})
      .map(([k, val]) => `${k}: ${val}`)
      .join(" | ");

  const header = [
    "Project Role", "Syria Relation",
    "Resides in Syria", "Residence Country",
    "Governorate", "Age", "Gender",
    "Working on Project", "Project Duration", "Project Stage",
    "Project Type", "Project Sector", "Description",
    "Women Ownership", "Legal Status", "Registration MM/YYYY",
    "Sales Change vs Last Year",
    "Received Funding", "Funding Types", "Funding Sources", "Funding Obstacles",
    "Startup Difficulty", "Challenge Grid",
    "Top Challenges", "Needed Services",
    "Target Customers", "Customer Locations",
    "Acquisition Channels", "Acquisition Obstacles",
    "Recovery Importance (1-5)", "Recovery Domains",
    "Recovery Activity Status", "Main Contribution",
    "Business Experience (0-5)", "Hardest Skills",
    "Digital Tools",
    "Conditions Current Grid", "Conditions YoY Grid",
    "Support Received", "Program Results", "Needed Support",
    "Aware Gov Program", "Needed Gov Services", "Top Reforms",
    "Open Comment",
    "Consent Interview", "Consent Case Study", "Contact Info",
    "Submitted At",
  ].join(",");

  const body = rows.map((r) =>
    [
      r.projectRole, r.syriaRelation,
      r.residesInSyria ? "Yes" : "No", r.residenceCountry ?? "",
      r.projectGovernorate, r.age, r.gender,
      r.isWorkingOnProject ? "Yes" : "No", r.projectDuration, r.projectStage,
      r.projectType, r.projectSector, r.projectDescription,
      r.womenOwnership, r.legalStatus, r.registrationMonthYear ?? "",
      r.salesChange,
      r.receivedFunding ? "Yes" : "No", arr(r.fundingTypes), arr(r.fundingSources), arr(r.fundingObstacles),
      r.startupDifficulty, obj(r.challengeGrid),
      arr(r.topChallenges), arr(r.neededServices),
      arr(r.targetCustomers), arr(r.customerLocations),
      arr(r.acquisitionChannels), arr(r.acquisitionObstacles),
      r.recoveryImportance, arr(r.recoveryDomains),
      r.recoveryActivityStatus, arr(r.mainContribution),
      r.businessExperience, arr(r.hardestSkills),
      arr(r.digitalTools),
      obj(r.conditionsCurrentGrid), obj(r.conditionsYoyGrid),
      arr(r.supportReceived), arr(r.programResults), arr(r.neededSupport),
      r.awareGovProgram, arr(r.neededGovServices), arr(r.topReforms),
      r.openComment ?? "",
      r.consentInterview ? "Yes" : "No", r.consentCaseStudy ? "Yes" : "No", r.contactInfo ?? "",
      r.createdAt,
    ].map(esc).join(","),
  );

  const blob = new Blob(["﻿" + [header, ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `fnf_survey_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── View ─────────────────────────────────────────────────────────────────────

export default function FnfSurveyView() {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<FnfSurveyResponse | null>(null);

  const { data: stats } = useFnfSurveyStats();
  const { data: allData } = useFnfSurveyResponses({ page: 1, limit: 2000 });
  const { data: tableData, isLoading, isFetching, isError } = useFnfSurveyResponses({
    page, limit: LIMIT, search, governorate,
  });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteFnfSurveyResponse();

  const rows = tableData?.data ?? [];
  const meta = tableData ?? { total: 0, page: 1, limit: LIMIT };
  const totalPages = Math.max(1, Math.ceil(meta.total / LIMIT));
  const govOptions = useMemo(
    () => Object.keys(stats?.byGovernorate ?? {}).sort(),
    [stats],
  );

  return (
    <div className="space-y-6">
      {/* Stat cards ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Responses" value={stats?.total ?? 0} icon={ClipboardList} accent="bg-brand-orange" />
        <StatCard label="Avg. recovery importance" value={stats?.averageRecoveryImportance.toFixed(1) ?? "0.0"} icon={TrendingUp} accent="bg-brand-green" />
        <StatCard label="Governorates" value={govOptions.length} icon={MapPin} accent="bg-brand-teal" />
        <StatCard label="Consented follow-up" value={(stats?.consentedToInterview ?? 0) + (stats?.consentedToCaseStudy ?? 0)} icon={Users} accent="bg-brand-teal" />
      </div>

      {/* Filters ──────────────────────────────────────────────────── */}
      <div className="card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
          <div className="relative sm:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search description, contact, or comment…"
              className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={governorate}
            onChange={(e) => { setGovernorate(e.target.value); setPage(1); }}
            className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
          >
            <option value="">All governorates</option>
            {govOptions.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <button
            type="button"
            onClick={() => exportCsv(allData?.data ?? rows)}
            disabled={(allData?.data ?? rows).length === 0}
            className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-brand-navy border border-brand-border text-white text-sm font-semibold disabled:opacity-40"
          >
            <Download size={14} />
            CSV
          </button>
        </div>
      </div>

      {/* Tally cards ──────────────────────────────────────────────── */}
      {stats && stats.total > 0 && (
        <div className="grid gap-3 lg:grid-cols-3">
          <TallyCard title="Top challenges" data={stats.byTopChallenges} total={stats.total} />
          <TallyCard title="Needed support" data={stats.byNeededSupport} total={stats.total} />
          <TallyCard title="Top reforms" data={stats.byTopReforms} total={stats.total} />
        </div>
      )}

      {/* Sheet-view table ────────────────────────────────────────── */}
      {isLoading ? (
        <div className="card overflow-hidden">
          <TableSkeleton />
        </div>
      ) : isError ? (
        <div className="card p-6 text-sm text-brand-orange">
          Couldn&apos;t load responses.
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-6 text-sm text-brand-muted">
          No responses yet.
        </div>
      ) : (
        <>
          <FnfSheetTable
            rows={rows}
            onOpenDetail={setDetail}
            onDelete={deleteRow}
            isDeleting={isDeleting}
          />
          {totalPages > 1 && (
            <div className="card border-t-0 rounded-t-none px-4 py-3">
              <Pagination
                page={page}
                totalPages={totalPages}
                total={meta.total}
                limit={LIMIT}
                onPageChange={setPage}
                isFetching={isFetching}
              />
            </div>
          )}
        </>
      )}

      {/* Detail modal ────────────────────────────────────────────── */}
      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ── Tally card ─────────────────────────────────────────────────────────────

function TallyCard({
  title, data, total,
}: {
  title: string;
  data: Record<string, number>;
  total: number;
}) {
  const rows = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 8);
  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-white mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-brand-muted">No data yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.map(([k, n]) => {
            const pct = Math.round((n / total) * 100);
            return (
              <div key={k} className="text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-white truncate" title={k}>{k}</span>
                  <span className="text-brand-muted whitespace-nowrap">
                    {n} · {pct}%
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-brand-navy overflow-hidden">
                  <div className="h-full bg-brand-green rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Detail modal ───────────────────────────────────────────────────────────

function DetailModal({
  row, onClose,
}: {
  row: FnfSurveyResponse;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-3xl h-[92vh] sm:h-auto sm:max-h-[90vh] bg-brand-card border border-brand-border rounded-t-2xl sm:rounded-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-brand-border flex items-center justify-between">
          <div>
            <div className="text-white font-semibold">
              {row.projectGovernorate} · {row.projectSector}
            </div>
            <div className="text-xs text-brand-muted">
              {new Date(row.createdAt).toLocaleString()}
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-brand-muted hover:text-white text-lg"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
          <Group title="Section 1 · Consent + gate">
            <Line label="Role" value={row.projectRole} />
            <Line label="Syria relation" value={row.syriaRelation} />
          </Group>
          <Group title="Section 2 · Founder + project">
            <Line label="Resides in Syria" value={row.residesInSyria ? "Yes" : `No · ${row.residenceCountry ?? ""}`} />
            <Line label="Age" value={row.age} />
            <Line label="Gender" value={row.gender} />
            <Line label="Working on project" value={row.isWorkingOnProject ? "Yes" : "No"} />
            <Line label="Duration" value={row.projectDuration} />
            <Line label="Stage" value={row.projectStage} />
            <Line label="Type" value={row.projectType} />
            <Line label="Description" value={row.projectDescription} block />
            <Line label="Women ownership" value={row.womenOwnership} />
            <Line label="Legal status" value={row.legalStatus} />
            {row.registrationMonthYear && <Line label="Registered" value={row.registrationMonthYear} />}
            <Line label="Sales vs last year" value={row.salesChange} />
          </Group>
          <Group title="Section 3 · Financing">
            <Line label="Received funding" value={row.receivedFunding ? "Yes" : "No"} />
            {row.receivedFunding && <Line label="Types" value={row.fundingTypes.join(", ")} block />}
            {row.receivedFunding && <Line label="Sources" value={row.fundingSources.join(", ")} block />}
            <Line label="Obstacles (top 3)" value={row.fundingObstacles.join(", ")} block />
          </Group>
          <Group title="Section 4 · Challenges">
            <Line label="Difficulty" value={row.startupDifficulty} />
            <Line label="Top challenges (top 3)" value={row.topChallenges.join(", ")} block />
            <Line label="Needed services (top 3)" value={row.neededServices.join(", ")} block />
            <GridBlock title="Challenge impact grid" grid={row.challengeGrid} />
          </Group>
          <Group title="Section 5 · Customers">
            <Line label="Target customers" value={row.targetCustomers.join(", ")} block />
            <Line label="Locations" value={row.customerLocations.join(", ")} block />
            <Line label="Acquisition channels (top 3)" value={row.acquisitionChannels.join(", ")} block />
            <Line label="Acquisition obstacles (top 3)" value={row.acquisitionObstacles.join(", ")} block />
          </Group>
          <Group title="Section 6 · Recovery">
            <Line label="Importance (1–5)" value={row.recoveryImportance} />
            <Line label="Domains" value={row.recoveryDomains.join(", ")} block />
            <Line label="Activity status" value={row.recoveryActivityStatus} />
            <Line label="Main contribution (top 3)" value={row.mainContribution.join(", ")} block />
          </Group>
          <Group title="Section 7 · Skills & environment">
            <Line label="Business experience (0–5)" value={row.businessExperience} />
            <Line label="Hardest skills (top 3)" value={row.hardestSkills.join(", ")} block />
            <Line label="Digital tools" value={row.digitalTools.join(", ")} block />
            <GridBlock title="Conditions — current impact" grid={row.conditionsCurrentGrid} />
            <GridBlock title="Conditions — vs last year" grid={row.conditionsYoyGrid} />
          </Group>
          <Group title="Section 8 · Support">
            <Line label="Support received" value={row.supportReceived.join(", ")} block />
            <Line label="Program results" value={row.programResults.join(", ")} block />
            <Line label="Needed support (top 3)" value={row.neededSupport.join(", ")} block />
          </Group>
          <Group title="Section 9 · Government & reforms">
            <Line label="Aware of gov program" value={row.awareGovProgram} />
            <Line label="Needed gov services" value={row.neededGovServices.join(", ")} block />
            <Line label="Top reforms (top 3)" value={row.topReforms.join(", ")} block />
            {row.openComment && <Line label="Open comment" value={row.openComment} block />}
          </Group>
          <Group title="Section 10 · Follow-up">
            <Line label="Interview consent" value={row.consentInterview ? "Yes" : "No"} />
            <Line label="Case-study consent" value={row.consentCaseStudy ? "Yes" : "No"} />
            {row.contactInfo && <Line label="Contact" value={row.contactInfo} />}
          </Group>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-brand-green mb-2">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Line({ label, value, block }: { label: string; value: React.ReactNode; block?: boolean }) {
  if (block) {
    return (
      <div>
        <div className="text-xs text-brand-muted mb-1">{label}</div>
        <div className="rounded-lg bg-brand-navy border border-brand-border p-2.5 text-white whitespace-pre-wrap" dir="rtl">
          {value || "—"}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs text-brand-muted">{label}</span>
      <span className="text-white text-end">{value ?? "—"}</span>
    </div>
  );
}

function GridBlock({ title, grid }: { title: string; grid: Record<string, string> }) {
  return (
    <div>
      <div className="text-xs text-brand-muted mb-1">{title}</div>
      <div className="rounded-lg bg-brand-navy border border-brand-border p-2.5" dir="rtl">
        {Object.entries(grid).map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 border-b border-brand-border py-1 last:border-b-0">
            <span className="text-white truncate">{k}</span>
            <span className="text-brand-muted text-xs shrink-0">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
